/*
 * The words and shapes mail clients use to introduce quoted history: attribution lines ("On … wrote:"),
 * classic Outlook header blocks and "Original Message" separators, in the languages the pilot's mail
 * arrives in. One list for both readers of it: the rep's reading frame, which folds quoted history
 * away (`apps/rep-workspace/src/quoted-history.ts`), and the drafting pipeline, which gives stages
 * the author's own text (`email-body.ts`). Pure: no imports.
 */

// Header labels in the languages Outlook localises them into. French puts a space before the colon.
const FROM_LABEL = "from|von|de|od|van|da|från|fra|lähettäjä|від|от";
const SENT_LABEL =
  "sent|date|gesendet|datum|envoyé|wysłano|data|verzonden|inviato|skickat|sendt|enviado|fecha|lähetetty|надіслано|отправлено";
const TO_OR_SUBJECT_LABEL =
  "to|an|à|a|do|aan|till|til|para|vastaanottaja|subject|betreff|objet|temat|onderwerp|oggetto|ämne|emne|asunto|assunto|aihe|тема";
export const HEADER_START = new RegExp(`^\\*?(?:${FROM_LABEL})\\*?\\s?:`, "iu");
const HEADER_SENT = new RegExp(`(?:^|\\n)\\*?(?:${SENT_LABEL})\\*?\\s?:`, "iu");
const HEADER_OTHER = new RegExp(`(?:^|\\n)\\*?(?:${TO_OR_SUBJECT_LABEL})\\*?\\s?:`, "iu");
export const HEADER_LINE = new RegExp(
  `^\\*?(?:${FROM_LABEL}|${SENT_LABEL}|${TO_OR_SUBJECT_LABEL}|cc)\\*?\\s?:`,
  "iu",
);

// The words inside a dashed separator line: a reply's quoted original, and a forwarded message.
const ORIGINAL_WORDS =
  "original message|ursprüngliche nachricht|message d'origine|oryginalna wiadomość|oorspronkelijk bericht|messaggio originale|mensaje original|mensagem original|ursprungligt meddelande|opprinnelig melding";
const FORWARDED_WORDS =
  "forwarded message|wiadomość przekazana dalej|weitergeleitete nachricht|message transféré";
export const ORIGINAL_MESSAGE = new RegExp(
  `^-{2,}\\s*(?:${ORIGINAL_WORDS}|${FORWARDED_WORDS})\\s*-{2,}$`,
  "iu",
);

/*
 * Read only to tell a forward from a reply (`forward-recognition.ts`), never to fold or cut text.
 * A separator naming a forwarded message, or Apple Mail's line opening one. A reply's own
 * separators (`ORIGINAL_MESSAGE` without these) and Outlook's header block open replies and
 * forwards alike, so they say nothing here.
 */
const FORWARDED_SEPARATOR = new RegExp(`^-{2,}\\s*(?:${FORWARDED_WORDS})\\s*-{2,}$`, "iu");
const FORWARDED_INTRO =
  /^(?:begin forwarded message|anfang der weitergeleiteten nachricht|début du message réexpédié|inicio del mensaje reenviado|begin doorgestuurd bericht|inizio messaggio inoltrato)\s?:$/iu;
/** A line that opens a forwarded message rather than a quoted reply. */
export function isForwardedMessageStart(line: string): boolean {
  return FORWARDED_SEPARATOR.test(line) || FORWARDED_INTRO.test(line);
}
/*
 * The subject prefixes mail clients put on a forward: Fwd/Fw, and Outlook's localised WG (de),
 * TR (fr), RV (es), PD (pl), ENC (pt), Doorst (nl), VB (sv), VL (fi). Left out on purpose: VS,
 * a forward in Danish and Norwegian but a reply in Finnish, and Italian's one-letter I.
 */
export const FORWARD_SUBJECT = /^\s*(?:fwd?|wg|tr|rv|pd|enc|doorst|vb|vl)\s*:/iu;

// "On … wrote:" and its localised forms. Each must end the line with a colon.
const ATTRIBUTION = [
  /^on\b.+\bwrote\s?:$/iu,
  /^am\b.+\bschrieb\b.*:$/iu,
  /^le\b.+\ba écrit\s?:$/iu,
  /^el\b.+\bescribió\s?:$/iu,
  /^il\b.+\bha scritto\s?:$/iu,
  /^op\b.+\bschreef\b.*:$/iu,
  /^(?:em|no dia)\b.+\bescreveu\s?:$/iu,
  /^(?:w dniu|dnia)\b.+\b(?:pisze|napisał\(a\)|napisała|napisał)\s?:$/iu,
  /^den\b.+\bskrev\b.*:$/iu,
];

/** An attribution line: localised "On … wrote:", with a digit, since a real one carries a date. */
export function isAttribution(line: string): boolean {
  return line.length <= 400 && /\d/.test(line) && ATTRIBUTION.some((rule) => rule.test(line));
}

/** A classic Outlook header block: `From:` first, then a `Sent:`/`Date:` and a `To:`/`Subject:` field. */
export function isOutlookHeader(text: string): boolean {
  return (
    text.length <= 1_200 &&
    HEADER_START.test(text) &&
    HEADER_SENT.test(text) &&
    HEADER_OTHER.test(text)
  );
}
